import z from "zod";
import { sizeFormSchema } from "../size";
import { weightFormSchema } from "../weight";
import { ZodStringNullable } from "../zod-types/zod-string-nullable";

/**
 * A load carrier — a pallet, a cage, a stillage. Warehouse stock that carries the goods
 * rather than being the goods, which is why it has dimensions and a balance but no article group
 * and no shelf: a carrier is counted in the yard, not located in it.
 *
 * The name is the identifier. Articles are keyed by a group/type/number triple, carriers by what
 * everybody in the yard already calls them, unique per organisation.
 *
 * `homeZoneId` is where this carrier stands when it is empty, and it is the one thing that gives
 * a Leergut round an origin. A round derived from articles picks in the zone the articles are
 * stocked in; a round of empties alone has no article zone at all, so its transport starts from
 * the carrier's own home. Null leaves such a carrier inert — it ships, but nobody is asked to
 * carry it anywhere.
 */
export const loadCarrierFormSchema = z
	.object({
		name: z.string().min(1),
		description: ZodStringNullable,
		warningStockBalance: z.coerce.number().min(0),
		minimumStockBalance: z.coerce.number().min(0),
		homeZoneId: ZodStringNullable,
	})
	.and(sizeFormSchema)
	.and(weightFormSchema)
	.superRefine((values, context) => {
		if (values.warningStockBalance < values.minimumStockBalance) {
			context.addIssue({
				code: "custom",
				message: "Warning stock balance must be greater or equal to minimum stock balance",
				path: ["warningStockBalance"],
			});
		}
	});

export type LoadCarrierFormSchema = z.infer<typeof loadCarrierFormSchema>;
